import { useState, useCallback } from 'react';
import { message } from 'antd';
import { t207Api, FT170, FT171, FT172, FT173, FT174 } from '../../../services/t207Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useT207Mode';

export function useT207Record() {
  const [record, setRecord] = useState<FT170 | null>(null);
  const [formData, setFormData] = useState<Partial<FT170>>({});
  const [attendees, setAttendees] = useState<Partial<FT171>[]>([]);
  const [agendas, setAgendas] = useState<Partial<FT172>[]>([]);
  const [vendorPeople, setVendorPeople] = useState<Partial<FT174>[]>([]);
  const [loading, setLoading] = useState(false);
  const { clearFieldErrors, getFieldError, setFieldErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{ data: FT170 } | null> => {
    setLoading(true);
    try {
      const data = await t207Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      setAttendees(data.attendees || []);
      setAgendas(data.agendas || []);
      setVendorPeople(data.vendor_people || []);
      return { data };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FT170, value: FT170[keyof FT170]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FT170): FT170[keyof FT170] => {
    return (formData as Record<string, FT170[keyof FT170]>)[key] ?? '';
  }, [formData]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FT170) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FT170,
        attendees: attendees as FT171[],
        agendas: agendas as FT172[],
        vendor_people: vendorPeople as FT174[],
      };
      if (mode === 'add') {
        const result = await t207Api.create(req);
        message.success(`新增成功: ${result.ft170_01}`);
        onSuccess(result);
      } else {
        const result = await t207Api.update(formData.ft170_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setAttendees(result.attendees || []);
        setAgendas(result.agendas || []);
        setVendorPeople(result.vendor_people || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, attendees, agendas, vendorPeople, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await t207Api.delete(record.ft170_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  // --- Attendees (FT171) ---
  const addAttendeeRow = useCallback(() => {
    const maxSeq = attendees.reduce((max, d) => Math.max(max, d.ft171_02 || 0), 0);
    setAttendees(prev => [...prev, { ft171_02: maxSeq + 1, ft171_01: formData.ft170_01 || '' }]);
    // Update count
    setFormData(prev => ({ ...prev, ft170_18: (prev.ft170_18 || 0) + 1 }));
  }, [attendees, formData.ft170_01]);

  const deleteAttendeeRow = useCallback((idx: number) => {
    setAttendees(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, ft171_02: i + 1 }));
    });
    setFormData(prev => ({ ...prev, ft170_18: Math.max(0, (prev.ft170_18 || 0) - 1) }));
  }, []);

  const setAttendeeField = useCallback((idx: number, key: keyof FT171, value: FT171[keyof FT171]) => {
    setAttendees(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  // --- Agendas (FT172) ---
  const addAgendaRow = useCallback(() => {
    const maxSeq = agendas.reduce((max, d) => Math.max(max, d.ft172_02 || 0), 0);
    setAgendas(prev => [...prev, { ft172_02: maxSeq + 1, ft172_01: formData.ft170_01 || '' }]);
    setFormData(prev => ({ ...prev, ft170_19: (prev.ft170_19 || 0) + 1 }));
  }, [agendas, formData.ft170_01]);

  const deleteAgendaRow = useCallback((idx: number) => {
    setAgendas(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, ft172_02: i + 1 }));
    });
    setFormData(prev => ({ ...prev, ft170_19: Math.max(0, (prev.ft170_19 || 0) - 1) }));
  }, []);

  const setAgendaField = useCallback((idx: number, key: keyof FT172, value: FT172[keyof FT172]) => {
    setAgendas(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  // --- Tracking Items (FT173) — nested under agendas ---
  const addTrackingItem = useCallback((agendaIdx: number) => {
    setAgendas(prev => {
      const next = [...prev];
      const agenda = next[agendaIdx];
      if (!agenda) return prev;
      const items = agenda.tracking_items || [];
      const maxSeq = items.reduce((max, d) => Math.max(max, d.ft173_03 || 0), 0);
      next[agendaIdx] = {
        ...agenda,
        tracking_items: [...items, {
          ft173_01: formData.ft170_01 || '',
          ft173_02: agenda.ft172_02 || 0,
          ft173_03: maxSeq + 1,
          ft173_11: '',
          ft173_12: null,
          ft173_13: '',
          ft173_14: null,
          ft173_15: '',
          ft173_15a: '',
          ft173_16: '',
          ft173_16a: '',
        }],
      };
      return next;
    });
  }, [formData.ft170_01]);

  const deleteTrackingItem = useCallback((agendaIdx: number, trackIdx: number) => {
    setAgendas(prev => {
      const next = [...prev];
      const agenda = next[agendaIdx];
      if (!agenda) return prev;
      const items = (agenda.tracking_items || []).filter((_, i) => i !== trackIdx);
      next[agendaIdx] = {
        ...agenda,
        tracking_items: items.map((d, i) => ({ ...d, ft173_03: i + 1 })),
      };
      return next;
    });
  }, []);

  const setTrackingField = useCallback((agendaIdx: number, trackIdx: number, key: keyof FT173, value: FT173[keyof FT173]) => {
    setAgendas(prev => {
      const next = [...prev];
      const agenda = next[agendaIdx];
      if (!agenda) return prev;
      const items = [...(agenda.tracking_items || [])];
      if (items[trackIdx]) {
        items[trackIdx] = { ...items[trackIdx], [key]: value };
      }
      next[agendaIdx] = { ...agenda, tracking_items: items };
      return next;
    });
  }, []);

  // --- Vendor People (FT174) ---
  const addVendorRow = useCallback(() => {
    const maxSeq = vendorPeople.reduce((max, d) => Math.max(max, d.ft174_02 || 0), 0);
    setVendorPeople(prev => [...prev, { ft174_02: maxSeq + 1, ft174_01: formData.ft170_01 || '' }]);
    setFormData(prev => ({ ...prev, ft170_25: (prev.ft170_25 || 0) + 1 }));
  }, [vendorPeople, formData.ft170_01]);

  const deleteVendorRow = useCallback((idx: number) => {
    setVendorPeople(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, ft174_02: i + 1 }));
    });
    setFormData(prev => ({ ...prev, ft170_25: Math.max(0, (prev.ft170_25 || 0) - 1) }));
  }, []);

  const setVendorField = useCallback((idx: number, key: keyof FT174, value: FT174[keyof FT174]) => {
    setVendorPeople(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], [key]: value };
      }
      return next;
    });
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setAttendees(record.attendees || []);
      setAgendas(record.agendas || []);
      setVendorPeople(record.vendor_people || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      ft170_13: today,
      ft170_18: 0,
      ft170_19: 0,
      ft170_25: 0,
    });
    setAttendees([]);
    setAgendas([]);
    setVendorPeople([]);
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FT170) => {
    setRecord(result);
    setFormData({ ...result });
    setAttendees(result.attendees || []);
    setAgendas(result.agendas || []);
    setVendorPeople(result.vendor_people || []);
  }, []);

  return {
    record, formData, attendees, agendas, vendorPeople, loading,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    // Attendees
    setAttendees, addAttendeeRow, deleteAttendeeRow, setAttendeeField,
    // Agendas
    setAgendas, addAgendaRow, deleteAgendaRow, setAgendaField,
    // Tracking Items
    addTrackingItem, deleteTrackingItem, setTrackingField,
    // Vendor People
    setVendorPeople, addVendorRow, deleteVendorRow, setVendorField,
    // Mode helpers
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  };
}
